// framlLayoutToggle.js
// -----------------------------------------------------------------------------
// Resolves which FRAML-P alert-detail layout to render:
//   'legacy' -> existing SmartAssessment view (default; unchanged behaviour)
//   'new'    -> FramlAlertDetailShell (the FIN-11304 "Spatial View" redesign)
//
// The production switch is the tenant dyn-property `framl_alert_detail_layout`
// (BE-2 in the FIN-11304 playbook). Until the backend seeds that property, this
// helper DEFAULTS TO LEGACY so no tenant sees the new view by accident, while
// letting us preview the redesign locally during development via either:
//   - URL query param:  ?framlLayout=new   (or ?framlLayout=legacy)
//   - localStorage:     framl_alert_detail_layout = 'new' | 'legacy'
//
// When BE-2 lands, pass the real dyn-property value in as `tenantLayout` from
// the router's mapStateToProps and it will take precedence over the default
// (dev overrides still win locally, which is fine — they are unreachable in a
// deployed build unless someone sets them explicitly).
// -----------------------------------------------------------------------------

export const FRAML_LAYOUT = {
  LEGACY: 'legacy',
  NEW: 'new'
};

const VALID = [FRAML_LAYOUT.LEGACY, FRAML_LAYOUT.NEW];

const normalise = (value) => {
  if (typeof value !== 'string') return undefined;
  const v = value.trim().toLowerCase();
  return VALID.includes(v) ? v : undefined;
};

// Local dev-only override. Returns undefined outside a browser or when unset.
const readDevOverride = () => {
  if (typeof window === 'undefined') return undefined;
  try {
    const fromUrl = normalise(
      new URLSearchParams(window.location.search).get('framlLayout')
    );
    if (fromUrl) return fromUrl;
    return normalise(window.localStorage.getItem('framl_alert_detail_layout'));
  } catch (e) {
    return undefined;
  }
};

/**
 * PER-DEPLOYMENT SWITCH, from `config.js`.
 *
 * `config.js` is a plain <script> copied to dist/ rather than bundled, so it is
 * edited per environment and takes effect on reload with NO REBUILD — the same
 * mechanism as `tenantId`, `keycloakRealm` and `downloadTimezoneMode`. Each
 * client has its own deployment, so this is tenant-level in practice.
 *
 * ADDED because the backend dyn-property (BE-2) does not exist: there is no
 * `framl_alert_detail_layout` row in `dyn_properties`, and no seed or migration
 * for one anywhere in the repo. Without this, the ONLY way to reach the Spatial
 * View is the `?framlLayout=new` dev override, which means no client can be put
 * on it at all.
 *
 * DELIBERATELY BELOW the dyn-property: if BE-2 ever lands, the server value
 * wins and this becomes the fallback. Nothing here has to be undone.
 */
const readTenantConfig = () => {
  if (typeof window === 'undefined') return undefined;
  return normalise(window.framlAlertDetailLayout);
};

/**
 * @param {string} [tenantLayout] value of the `framl_alert_detail_layout`
 *   dyn-property, once BE-2 wires it through. Optional today.
 * @returns {'legacy' | 'new'}
 */
export const resolveFramlLayout = (tenantLayout) =>
  /* THE DEFAULT IS SPATIAL — changed 2026-09-17 by the team lead: "let's make it
     default, and if someone wants to keep it classic, they can change in the
     frontend deployment to set var framlAlertDetailLayout = 'legacy' in
     config.js". So 'legacy' is now an explicit OPT-OUT, not the baseline.

     THIS REVERSES A DELIBERATE SAFETY DECISION. The previous fallback was LEGACY,
     defended in FramlAlertDetailRouter.layout.test.js as: "no tenant may land on
     the redesign by accident. Flipping this default would ship an unreviewed
     alert-detail page to every FRAML-P user at once." That is now the intent.
     Note QA has not executed its 558 cases, and 214 rows were written assuming
     the Spatial View is reached only via `?framlLayout=new` — those cases no
     longer need the parameter. Tell QA before they run.

     THE DEV OVERRIDE STILL COMES FIRST, and now matters in the other direction:
     `?framlLayout=legacy` is how you preview Classic on a deployment that has
     not opted out. */
  readDevOverride() ||
  normalise(tenantLayout) ||
  readTenantConfig() ||
  FRAML_LAYOUT.NEW;

export const isFramlNewLayout = (tenantLayout) =>
  resolveFramlLayout(tenantLayout) === FRAML_LAYOUT.NEW;

// -----------------------------------------------------------------------------
// Mock-fallback gate (FIN-11304)
//
// Some Spatial panels have no live endpoint yet (timeline = BE-1, Related Cases =
// BE-3, Custom Tables = FIN-11586), so they can render `alertDetailMock` instead.
//
// NOTE ON MECHANISM — this is a plain switch, NOT an empty-response fallback.
// FramlAlertDetailShell/index.js reads this flag and picks EITHER the fixture OR
// an empty shape; it never inspects a live payload and never substitutes the mock
// because a response came back empty. Do not "improve" it into
// `live.length ? live : mock`: an alert that legitimately has no activity would
// then render seven fabricated events (a login, five failed OTP attempts, a fund
// transfer), and an analyst could escalate or file a report on events that never
// occurred. Fabricated data that looks real is worse than an empty table.
//
// So the fallback is enabled ONLY when a dev layout override is present — i.e.
// when you are previewing the Spatial view locally via ?framlLayout=new or
// localStorage. In a deployed build the layout comes from the BE-2 dyn-property,
// no override exists, and an empty response renders an empty table.
//
// Force it either way with ?framlMock=on|off or
// localStorage `framl_mock_fallback` = 'on' | 'off'.
// -----------------------------------------------------------------------------

const normaliseOnOff = (value) => {
  if (typeof value !== 'string') return undefined;
  const v = value.trim().toLowerCase();
  if (v === 'on' || v === 'true') return true;
  if (v === 'off' || v === 'false') return false;
  return undefined;
};

const readMockOverride = () => {
  if (typeof window === 'undefined') return undefined;
  try {
    const fromUrl = normaliseOnOff(
      new URLSearchParams(window.location.search).get('framlMock')
    );
    if (fromUrl !== undefined) return fromUrl;
    return normaliseOnOff(window.localStorage.getItem('framl_mock_fallback'));
  } catch (e) {
    return undefined;
  }
};

export const isMockFallbackEnabled = () => {
  const explicit = readMockOverride();
  if (explicit !== undefined) return explicit;
  // Implicit: only while previewing the redesign through a dev override.
  return readDevOverride() !== undefined;
};

/**
 * True only for an EXPLICIT `?framlMock=on` / `framl_mock_fallback=on`.
 *
 * The difference from isMockFallbackEnabled() matters: that one also returns true
 * implicitly whenever the redesign is being previewed, and is used as a fallback
 * for an EMPTY live response. This one is the caller's licence to prefer the
 * fixture OVER live data — needed to demo design work that only exists in the
 * fixture (the SCENARIO selector, until BE-1 ships scenarios).
 *
 * Kept explicit-only on purpose: `?framlLayout=new` must keep showing real data,
 * or nobody would notice the live view breaking.
 */
export const isMockForced = () => readMockOverride() === true;
